import { storeToRefs } from "pinia"
import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller"
import { WorkspaceNote } from "@/workspace/models/workspace-note.model"
import type { IWorkspaceNote } from "@/workspace/interfaces/api/workspace-note.interface"
import { WorkspaceNoteRepository } from "@/workspace/repositories/workspace-note-repository"
import { useWorkspaceStore } from "@/workspace/store/workspace.store"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"

export class WorkspaceNoteController extends GenericModelController<
  WorkspaceNote,
  IWorkspaceNote,
  WorkspaceNoteRepository
> {
  private workspaceStore: ReturnType<typeof useWorkspaceStore>
  private missingNoteRetrievals = new Map<string, Promise<WorkspaceNote | undefined>>()

  constructor() {
    const workspaceStore = useWorkspaceStore()
    const { notes } = storeToRefs(workspaceStore)

    super(WorkspaceNote, { collection: notes }, new WorkspaceNoteRepository())
    this.workspaceStore = workspaceStore

    // Every note of the tenant arrives here; useWorkspaceData() only shows the visible ones.
    this.socketSynchronizationHandlers = useSocketSynchronizationHandlers(WorkspaceNote, notes)
  }

  /** Retrieve a note referenced by a checklist socket event, sharing concurrent requests for the same note. */
  public retrieveMissingNote(noteId: string): Promise<WorkspaceNote | undefined> {
    const existingNote = this.workspaceStore.notes.find((note) => note.id === noteId)
    if (existingNote) {
      return Promise.resolve(existingNote)
    }

    const existingRetrieval = this.missingNoteRetrievals.get(noteId)
    if (existingRetrieval) {
      return existingRetrieval
    }

    const retrieval = this.fetchMissingNote(noteId).finally(() => {
      this.missingNoteRetrievals.delete(noteId)
    })
    this.missingNoteRetrievals.set(noteId, retrieval)
    return retrieval
  }

  private async fetchMissingNote(noteId: string): Promise<WorkspaceNote | undefined> {
    try {
      const response = await this.apiFunctions.getRepository().get(noteId, {
        // Checklist messages are tenant-wide. Most missing parents are simply not visible to this planner.
        statusCodeHandlers: [403, 404].map((statusCode) => ({
          statusCode,
          callback: () => true
        }))
      })
      if (response.status === 403 || response.status === 404) {
        return undefined
      }
      return await this.storeFunctions.createModel(response.data)
    } catch (error) {
      console.error("Could not retrieve note referenced by a checklist socket message", error)
      return undefined
    }
  }
}
